import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { cancelMembership } from "../../lib/api";

/**
 * GracePeriodBanner — shown when the user has a `pending_cancel=true` flag
 * after clicking CANCEL MEMBERSHIP. Their PRO Player perks (verified ✓,
 * AI avatars, etc.) stay alive for 7 days; this banner counts down and
 * offers a one-tap re-up via Stripe.
 */
export function GracePeriodBanner({ user, onToast }) {
  const [busy, setBusy] = useState(false);
  if (!user?.pending_cancel) return null;
  const exp = user.paid_pro_expires_at ? new Date(user.paid_pro_expires_at) : null;
  if (!exp) return null;
  const msLeft = exp.getTime() - Date.now();
  if (msLeft <= 0) return null;
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
  const resub = async () => {
    setBusy(true);
    try {
      const { createSubscription } = await import("../../lib/api");
      const r = await createSubscription("pro_player", window.location.origin);
      if (r?.url) window.location.href = r.url;
      else onToast?.("Couldn't start checkout — retry.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't start checkout.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="dash-grace-banner"
      className="mt-3 rounded-2xl px-3 py-2 border border-[#14b8a6]/50 bg-[#14b8a6]/10 flex flex-wrap gap-2 items-center justify-between"
    >
      <p className="text-[11px] text-[#14b8a6]">
        <i className="fas fa-clock text-[#14b8a6] mr-1" />
        Your PRO Player perks expire in <b className="text-[#14b8a6]">{daysLeft} day{daysLeft === 1 ? "" : "s"}</b>.
        {" "}<span className="text-[#14b8a6]/80">Re-up to keep your ✓ + AI tools + priority queue.</span>
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={resub}
        data-testid="dash-grace-resubscribe"
        className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest font-bold bg-[#14b8a6] text-black hover:brightness-110 disabled:opacity-60"
      >
        {busy ? "OPENING…" : "RE-UP · $1.99"}
      </button>
    </div>
  );
}

/**
 * CancelMembershipButton — one-click downgrade for paying PRO Players.
 * Calls POST /api/billing/cancel (defaults to kind='player').
 */
export function CancelMembershipButton({ onToast }) {
  const { refresh, user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (user?.pending_cancel) return null;
  const onCancel = async () => {
    if (!window.confirm(
      "Cancel your PRO Player membership?\n\n" +
      "Your verified blue check, AI avatars, banner uploads and priority queue " +
      "will stay active for 7 more days, then deactivate. You can re-subscribe " +
      "any time from /pricing."
    )) return;
    setBusy(true);
    try {
      const r = await cancelMembership({ kind: "player" });
      await refresh?.();
      onToast?.(r?.message || "PRO Player cancelled — perks active 7 more days.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't cancel — try again.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onCancel}
      data-testid="dash-cancel-membership-btn"
      className="mt-4 text-[10px] tracking-[3px] font-bold text-white/70 hover:text-[#14b8a6] underline disabled:opacity-50"
    >
      {busy ? "CANCELLING…" : "CANCEL MEMBERSHIP"}
    </button>
  );
}
